// Склад (УТ) — правила разбора и сборки данных 1С. Чистые функции: ни React, ни сети.
//
// Логика повторяет stockSearch из peleng-portal/src/worker.js — это не «вдохновлялись»,
// а перенос проверенного поведения: там оно годами обслуживает реальный поиск по складу.
// Что оттуда важно и почему:
//   • искать надо и по названию, и по артикулу — половина запросов приходит артикулом;
//   • сортировать выдачу по остатку, а не по названию: при широком запросе позиция
//     с реальным остатком тонула в сотне нулевых, и человек считал, что её нет;
//   • раскладка клавиатуры: «rf,tkm» вместо «кабель» — обычное дело, ловим сами;
//   • остатки запрашиваются пачками по 12 ref: длинный OR-фильтр к Balance() даёт 404
//     (URI too long) — это найдено на бою, число не менять «на глаз».
//
// Чего здесь намеренно НЕТ: фаззи-поиска по триграммам с расстоянием Левенштейна (он есть
// в портале). Это ещё один поход в 1С на каждый неудачный запрос; вводить его стоит,
// когда станет видно, что промахов много.

/** Поля номенклатуры, которых хватает списку. Больше — лишний трафик из 1С. */
export const NOM_SELECT = 'Ref_Key,Description,Артикул,Качество,СрокГодности,Производитель_Key,IsFolder'

/** Сколько кандидатов просим у 1С. Как в портале: сортировки по остатку у 1С нет,
 *  поэтому набираем с запасом и расставляем приоритет у себя. */
export const NOM_TOP = 100

/** Ref в одном запросе к Balance(). Больше — 404 «URI too long» (проверено на бою). */
export const REF_CHUNK = 12

/**
 * Для скольких найденных позиций спрашиваем остатки. Каждые 12 ref — отдельный запрос
 * в 1С, а у ядра всего ODATA_MAX_INFLIGHT=4 одновременных: выкачивать остатки для сотни
 * кандидатов значит занять всю дверь в 1С одним поиском. Остальные позиции показываем
 * без остатка и честно об этом пишем.
 */
export const BALANCE_LIMIT = 36

export interface Nomenclature {
  Ref_Key: string
  Description?: string
  Артикул?: string
  Качество?: string
  СрокГодности?: number | string
  Производитель_Key?: string
  IsFolder?: boolean
}

export interface BalanceRow {
  Номенклатура_Key?: string
  Склад_Key?: string
  ВНаличииBalance?: number | string
}

export interface ReserveRow {
  Номенклатура_Key?: string
  ВРезервеBalance?: number | string
}

export interface RefName {
  Ref_Key: string
  Description?: string
}

/** Остаток на одном складе. */
export interface WarehouseQty {
  name: string
  qty: number
}

/** Строка выдачи склада. */
export interface StockItem {
  ref: string
  name: string
  art: string
  /** Всего на складах. null — остаток не запрашивали (позиция за пределами BALANCE_LIMIT). */
  have: number | null
  reserve: number | null
  /** Свободно = всего − резерв. null, если остаток не запрашивали. */
  free: number | null
  byWarehouse: WarehouseQty[]
  quality: string
  /** Качество не «новое» — б/у, восстановленное. Важно для снабженца. */
  used: boolean
  shelfDays: number
  producer: string
}

/** Гасит хвосты float: 28.599999999 → 28.6. */
export const num3 = (x: unknown): number => Math.round((Number(x) || 0) * 1000) / 1000

/** Количество на складе: не деньги, дробное, без валюты. */
export function fmtQty(v: number | null | undefined): string {
  if (v === null || v === undefined || Number.isNaN(v)) return '—'
  return v.toLocaleString('ru-RU', { maximumFractionDigits: 3 })
}

/** Строковый литерал OData: одинарная кавычка удваивается. */
export const q1c = (s: string): string => `'${String(s).replace(/'/g, "''")}'`

const GUID_RE = /^[0-9a-fA-F-]{36}$/
const EMPTY_GUID = '00000000-0000-0000-0000-000000000000'

/** Отбрасываем всё, что не похоже на ref: в $filter это уходит подстановкой. */
export const isRef = (v: unknown): v is string =>
  typeof v === 'string' && GUID_RE.test(v) && v !== EMPTY_GUID

/** `Поле eq guid'…' or Поле eq guid'…'` — 1С не понимает in, только цепочку or. */
export const guidOrFilter = (field: string, refs: string[]): string =>
  refs.filter(isRef).map((r) => `${field} eq guid'${r}'`).join(' or ')

export function chunk<T>(arr: T[], size: number): T[][] {
  const out: T[][] = []
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size))
  return out
}

// ─────────── путь OData ───────────
// Кодируем сами и осознанно: ядро путь НЕ перекодирует (иначе ломается кириллица
// в $filter), значит корректность кодирования — наша забота.

/** Имя сущности в пути. Подресурс через `/` сохраняем: `…ТоварыНаСкладах/Balance()`. */
const encodeResource = (resource: string): string =>
  resource.split('/').map((seg) => encodeURIComponent(seg)).join('/')

/**
 * `Catalog_Номенклатура?$filter=…&$top=100`. Значения кодируем encodeURIComponent —
 * ровно как порталы, это проверенная форма. Запятые в $select возвращаем как есть:
 * там они разделитель полей, а не часть значения.
 */
export function odataPath(resource: string, params: Record<string, string | number | undefined>): string {
  const parts: string[] = []
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === '') continue
    parts.push(`${k}=${encodeURIComponent(String(v)).replace(/%2C/g, ',')}`)
  }
  return encodeResource(resource) + (parts.length ? `?${parts.join('&')}` : '')
}

// ─────────── раскладка клавиатуры ───────────
const KB_EN = "qwertyuiop[]asdfghjkl;'zxcvbnm,./"
const KB_RU = 'йцукенгшщзхъфывапролджэячсмитьбю.'

const swap = (s: string, from: string, to: string): string => {
  let out = ''
  for (const ch of s.toLowerCase()) {
    const i = from.indexOf(ch)
    out += i >= 0 ? to[i] : ch
  }
  return out
}

/** «rf,tkm» → «кабель». Пустой массив, если исправлять нечего. */
export function layoutVariants(q: string): string[] {
  const src = q.trim().toLowerCase()
  const out: string[] = []
  for (const cand of [swap(src, KB_EN, KB_RU), swap(src, KB_RU, KB_EN)]) {
    if (cand && cand !== src && !out.includes(cand)) out.push(cand)
  }
  return out
}

// ─────────── поиск ───────────

/**
 * Условие поиска: каждое слово должно найтись в названии ИЛИ в артикуле, слова — через И.
 * «кабель 25» находит «Кабель КГ 25» и не находит «Кабель КГ 16».
 */
export function searchFilter(q: string): string {
  const toks = q.trim().split(/\s+/).filter(Boolean).slice(0, 6)
  if (!toks.length) return ''
  return toks
    .map((t) => `(substringof(${q1c(t)},Description) or substringof(${q1c(t)},Артикул))`)
    .join(' and ')
}

export interface StockAgg {
  have: number
  reserve: number
  wh: Record<string, number>
}

/**
 * Остатки и резервы → карта по ref. Имя склада берём из справочника; неизвестный ref
 * склада показываем как «склад», а не пустой строкой — иначе колонка выглядит сломанной.
 */
export function aggregate(
  balances: BalanceRow[], reserves: ReserveRow[], warehouses: Record<string, string>,
): Record<string, StockAgg> {
  const agg: Record<string, StockAgg> = {}
  const slot = (ref: string): StockAgg => (agg[ref] = agg[ref] || { have: 0, reserve: 0, wh: {} })

  for (const row of balances) {
    const ref = row.Номенклатура_Key
    const qty = Number(row.ВНаличииBalance || 0)
    if (!ref || !qty) continue
    const a = slot(ref)
    a.have += qty
    const wname = (row.Склад_Key && warehouses[row.Склад_Key]) || 'склад'
    a.wh[wname] = (a.wh[wname] || 0) + qty
  }
  for (const row of reserves) {
    const ref = row.Номенклатура_Key
    const qty = Number(row.ВРезервеBalance || 0)
    if (!ref || !qty) continue
    slot(ref).reserve += qty
  }
  return agg
}

/** Строка выдачи. `agg === undefined` — остатки для этой позиции не запрашивали. */
export function toStockItem(
  n: Nomenclature, agg: StockAgg | undefined, known: boolean, producers: Record<string, string>,
): StockItem {
  const quality = (n.Качество || '').trim()
  const have = known ? num3(agg?.have ?? 0) : null
  const reserve = known ? num3(agg?.reserve ?? 0) : null
  return {
    ref: n.Ref_Key,
    name: n.Description || '',
    art: n.Артикул || '',
    have,
    reserve,
    free: have === null || reserve === null ? null : num3(have - reserve),
    byWarehouse: Object.entries(agg?.wh || {})
      .filter(([, v]) => v)
      .map(([name, qty]) => ({ name, qty: num3(qty) }))
      .sort((a, b) => b.qty - a.qty),
    quality,
    used: !!quality && !/нов/i.test(quality),
    shelfDays: Number(n.СрокГодности || 0) || 0,
    producer: (n.Производитель_Key && producers[n.Производитель_Key]) || '',
  }
}

/** Сначала то, что реально лежит на складе, потом нули, потом позиции без остатков. */
export function byStock(a: StockItem, b: StockItem): number {
  const known = Number(b.have !== null) - Number(a.have !== null)
  if (known) return known
  return ((b.free ?? 0) - (a.free ?? 0))
    || ((b.have ?? 0) - (a.have ?? 0))
    || a.name.localeCompare(b.name, 'ru')
}
